// Growem — rest-band resolver + position map for the count-up rest timer.
// A band is {min, ideal, max} seconds. Explicit Exercise.restMin/restMax win; otherwise the
// band is derived from the "desc …" segment of the exercise meta (e.g. "desc 2–3 min").

export interface RestBand { min: number; ideal: number; max: number }

/** Parse the "desc …" segment of meta into [lo, hi] seconds, or null. */
function parseMeta(meta: string): [number, number] | null {
  const seg = (meta.split('·').find((s) => /desc/i.test(s)) || '').toLowerCase()
  if (!seg) return null
  const isMin = /\bmin\b/.test(seg)
  const toSec = (t: string) => t.includes(':')
    ? (+t.split(':')[0]) * 60 + (+t.split(':')[1])
    : isMin ? Math.round(parseFloat(t.replace(',', '.')) * 60) : Math.round(parseFloat(t))
  const nums = (seg.match(/\d+(?::\d{2})?/g) || []).map(toSec)
  return nums.length >= 2 ? [Math.min(...nums), Math.max(...nums)] : null
}

/** Build a band from the ideal + meta; used as the runtime fallback when fields are absent. */
export function deriveBand(ex: { rest: number; meta: string }): RestBand {
  const ideal = ex.rest
  const p = parseMeta(ex.meta)
  if (p) return { min: p[0], ideal: Math.min(Math.max(ideal, p[0]), p[1]), max: p[1] }
  const b = Math.max(15, Math.round(ideal * 0.15)) // single value → ±15% (min 15s)
  return { min: ideal - b, ideal, max: ideal + b }
}

/** Resolver used by the session: explicit fields win, else derive. Guarantees min < ideal < max. */
export function restBand(ex: {
  rest: number; meta: string; restMin?: number; restIdeal?: number; restMax?: number
}): RestBand {
  let min = ex.restMin, max = ex.restMax
  const ideal = ex.restIdeal ?? ex.rest
  if (min == null || max == null) { const d = deriveBand(ex); min ??= d.min; max ??= d.max }
  if (max <= min) max = min + 30 // degenerate-data guard
  return { min, ideal: Math.min(Math.max(ideal, min), max), max }
}

// Fixed-share position map: a fat, always-legible central ideal band regardless of window length.
const UNDER = 38, IDEAL = 42 // OVER = 20 implied; boundary ticks at 38% and 80%

/** Fraction 0..1 of the meter filled at elapsed `t` (seconds). */
export function frac(t: number, { min, max }: RestBand): number {
  if (t <= min) return (t / min) * UNDER / 100
  if (t <= max) return (UNDER + (t - min) / (max - min) * IDEAL) / 100
  return Math.min(1, (80 + (t - max) / (max - min) * 20) / 100) // over-runway = one window, then pins
}

/** Percent position (0..100) of the ideal marker on the meter. */
export function idealPct(b: RestBand): number {
  return UNDER + (b.ideal - b.min) / (b.max - b.min) * IDEAL
}

export function zoneOf(t: number, b: RestBand): 'under' | 'ideal' | 'over' {
  return t < b.min ? 'under' : t <= b.max ? 'ideal' : 'over'
}
